<template>
  <KalpCalculation
    :kalp="calculation"
    :details="details"
    :is-topup-loan="application?.type === 'topupLoanApplication'"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { KalpCalculation, type KalpCalculationData, type KalpCalculationDetails } from '@staff/ui'
import { formatAmountWithCurrency } from '@staff/shared'
import type {
  DistributorApplicationDetails,
  KalpCalculation as ApplicationKalpCalculation,
} from '@/services/application'
import type { DistributorCommitmentDetails } from '@/services/commitment'

const props = defineProps<{
  application: DistributorApplicationDetails | DistributorCommitmentDetails
  kalp: ApplicationKalpCalculation
}>()

const { t } = useI18n()

const calculation = computed<KalpCalculationData>(() => ({
  ...props.kalp,
  incomeModels: props.kalp.incomeModels.map((incomeModel) => {
    const applicant = props.application?.applicants.find((a) => a.userId === incomeModel.userId)
    return {
      ...incomeModel,
      name: applicant ? `${applicant.givenName} ${applicant.surname}`.trim() || 'Saknar låntagare' : 'Saknar låntagare',
    }
  }),
}))

// Input details shown as explanatory subtext under the calculation rows they feed into.

const otherPropertiesDetail = computed(() => {
  if (props.application?.type === 'commitmentApplication' && !props.application.household) {
    return 'Detaljer om övriga boenden kan inte visas här'
  }
  const kept = (props.application?.household?.otherProperties ?? []).filter((op) => op.keepProperty)
  if (!kept.length) {
    return 'Inga övriga boenden'
  }
  return kept
    .map(
      (prop) =>
        `${t(`PROPERTY_TYPE.${prop.type}`)}${prop.monthlyFee != null ? ` ${formatAmountWithCurrency(prop.monthlyFee)}` : ''}`,
    )
    .join(' · ')
})

const householdDetail = computed(() => {
  if (props.application?.type === 'commitmentApplication' && !props.application.household) {
    return 'Hushållsdetaljer kan inte visas här'
  }
  // DAPI leaves out a household list that was never given, so each may be missing
  const adults = (props.application?.applicants.length ?? 0) + (props.application?.household?.otherAdults?.length ?? 0)
  const children = props.application?.household?.children?.length ?? 0
  return `${adults} vuxna · ${children} barn`
})

const collateralPropertyDetail = computed(() => {
  // Only a full application has a collateral property; a commitment has an estimate instead.
  const property =
    props.application && props.application.type !== 'commitmentApplication'
      ? props.application.collateralProperty
      : null
  if (!property) {
    const estimation =
      props.application && 'propertyEstimation' in props.application ? props.application.propertyEstimation : null

    if (estimation?.type) {
      return `${t(`PROPERTY_TYPE.${estimation.type}`)} · ${estimation.municipality ?? 'Kommun saknas'} (uppskattning)`
    }
    return 'Uppgift om bostaden saknas'
  }
  return `${t(`PROPERTY_TYPE.${property.type}`)} · ${property.address.municipality.name}`
})

const details = computed<KalpCalculationDetails>(() => ({
  otherProperties: otherPropertiesDetail.value,
  household: householdDetail.value,
  collateralProperty: collateralPropertyDetail.value,
}))
</script>
